import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Building, 
  Briefcase,
  HardHat,
  Key,
  Stethoscope,
  ShoppingBag,
  ArrowRight,
  Star,
  Clock,
  Shield
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema
} from '../utils/schemaGenerator';

const CommercialCleaningPage = () => {
  const commercialServices = [
    {
      icon: Briefcase,
      title: "Office Cleaning",
      description: "Professional spaces that make the right impression",
      features: [
        "Daily, weekly, or monthly contracts",
        "After-hours scheduling",
        "Flexible service plans",
        "Professional office suites & coworking spaces"
      ],
      link: "/services/commercial/office-cleaning",
      badge: "Popular"
    },
    {
      icon: Building,
      title: "Property Management",
      description: "Turnover cleaning and common area maintenance",
      features: [
        "Multi-unit turnover expertise",
        "Vacant unit preparation",
        "Common area cleaning",
        "Flexible scheduling for quick turnarounds"
      ],
      link: "/services/commercial/property-management",
      badge: null
    },
    {
      icon: Key,
      title: "Real Estate & Listing Prep",
      description: "Make homes show-ready for listings and open houses",
      features: [
        "Pre-listing deep cleaning",
        "Open house preparation",
        "Fast turnaround for closings",
        "Trusted by local realtors"
      ],
      link: "/services/commercial/real-estate-cleaning",
      badge: null
    },
    {
      icon: HardHat,
      title: "Construction & Post-Construction",
      description: "Final cleanup for builders and contractors",
      features: [
        "New construction final cleaning",
        "Renovation cleanup",
        "Punch list support",
        "Builder partnerships"
      ],
      link: "/services/commercial/construction-cleanup",
      badge: null
    },
    {
      icon: ShoppingBag,
      title: "Retail & Storefront",
      description: "Clean, welcoming spaces for your customers",
      features: [
        "High-traffic floor care",
        "Display and fixture cleaning",
        "Off-hours scheduling",
        "Beach community expertise"
      ],
      link: "/services/commercial/retail-cleaning",
      badge: null
    },
    {
      icon: Stethoscope,
      title: "Medical Facilities",
      description: "Healthcare-grade protocols for medical offices",
      features: [
        "Infection control standards",
        "Patient safety focus",
        "Compliance-ready processes",
        "Medical office & dental practices"
      ],
      link: "/services/commercial/medical-facility-cleaning",
      badge: "Specialty"
    }
  ];

  const whyBusinessesChooseUs = [
    {
      icon: Shield,
      title: "Fully Insured & Reliable",
      description: "Licensed, insured, and accountable. We understand the trust businesses place in their cleaning partners, and we take that responsibility seriously."
    },
    {
      icon: Star,
      title: "Quality You Can Count On",
      description: "With Heather's 15+ years of executive housekeeping experience, we bring hospitality-level standards to every commercial space we serve."
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "We work around your business hours—early mornings, evenings, weekends. Whatever keeps your operations running smoothly."
    },
    {
      icon: Building,
      title: "Local & Accessible",
      description: "Bob and Heather are right here in Palm Bay. You're not dealing with a corporate call center—you're working directly with the owners."
    }
  ];

  const industries = [
    "Professional Offices",
    "Property Management",
    "Real Estate Agencies",
    "Medical & Dental Offices",
    "Retail Stores & Boutiques",
    "Construction & Builders",
    "Coworking Spaces",
    "HOA & Condo Associations"
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Commercial Cleaning Services | Pierce Premier Cleaning",
      description: "Professional commercial cleaning in Brevard County, FL. Office cleaning, property management, construction cleanup, real estate prep, and more. Locally owned and trusted.",
      url: "/services/commercial"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Commercial Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Commercial Cleaning Services",
      description: "Comprehensive commercial cleaning services for offices, property management, real estate, construction, retail, and medical facilities throughout Brevard County."
    })
  );

  return (
    <>
      <SEO 
        title="Commercial Cleaning Services | Pierce Premier | Brevard County, FL"
        description="Trusted commercial cleaning in Brevard County. Office cleaning, property management turnover, real estate prep, construction cleanup. Locally owned, fully insured, 5-star rated."
        canonical="/services/commercial"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-blue-600 text-white mb-4">B2B Services</Badge>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Commercial Cleaning Services
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Professional cleaning solutions for Brevard County businesses. From offices and property management to construction cleanup and retail spaces, we deliver the consistent quality your business deserves.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Request Commercial Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional commercial cleaning services in Brevard County"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Services Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Commercial Services
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Tailored cleaning solutions for every type of business. One-time projects or ongoing contracts—we&apos;re flexible to meet your needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {commercialServices.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <Card key={idx} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 relative overflow-hidden">
                    {service.badge && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-blue-600 text-white">{service.badge}</Badge>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {service.description}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {service.features.map((feature, featureIdx) => (
                          <li key={featureIdx} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {service.link && (
                        <Link 
                          to={service.link} 
                          className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center"
                        >
                          Learn More
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Businesses Choose Us */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Brevard County Businesses Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                As a small business ourselves, we understand what matters to you: reliability, quality, and people you can trust.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {whyBusinessesChooseUs.map((reason, idx) => {
                const Icon = reason.icon;
                return (
                  <Card key={idx} className="border-2">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{reason.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Industries We Serve
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                From downtown Melbourne offices to beachside retail shops, we&apos;ve worked with businesses across Brevard County.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {industries.map((industry, idx) => (
                <div key={idx} className="p-4 bg-blue-50 rounded-lg text-center border border-blue-100">
                  <span className="text-gray-900 font-semibold">{industry}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Simple, straightforward, and built around your business needs.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Contact Us",
                  description: "Call or submit a quote request. Tell us about your space, frequency needs, and any special requirements."
                },
                {
                  step: "2",
                  title: "We Create a Plan",
                  description: "We'll discuss scope, schedule, and pricing. Most commercial clients get a custom proposal within 24-48 hours."
                },
                {
                  step: "3",
                  title: "We Get to Work",
                  description: "Flexible start dates, consistent quality, and direct communication. You'll work with Bob and Heather—not a middleman."
                }
              ].map((item, idx) => (
                <Card key={idx} className="border-2 text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serving Commercial Clients Throughout Brevard County
            </h2>
            <p className="text-gray-600 mb-8">
              Based in Palm Bay with service across the Space Coast—from Viera&apos;s tech corridor to Melbourne&apos;s medical district to beachside retail communities.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Palm Bay", "Melbourne", "Viera", "Rockledge",
                "West Melbourne", "Satellite Beach", "Cocoa Beach", 
                "Merritt Island", "Indialantic"
              ].map((city, idx) => (
                <Badge key={idx} variant="outline" className="text-sm py-2 px-4">
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-blue-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;We use Pierce Premier for our office cleaning every week. Always on time, always spotless. Highly recommend!&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">Michael Rodriguez</p>
                <p className="text-gray-500 text-sm">Melbourne, FL Business Owner</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Partner with Pierce Premier?"
          description="Let's discuss your commercial cleaning needs. Custom quotes, flexible contracts, and the reliability your business deserves."
          primaryButtonText="Request Commercial Quote"
        />
      </div>
    </>
  );
};

export default CommercialCleaningPage;
